/**
 * The delegated-event contract: which DOM event each `data-*` dispatch attribute
 * listens on.
 *
 * This is the *attribute* half of the view-wiring seam (`viewContract.ts` holds
 * the *name* half). It exists as data rather than code for one reason: the list
 * used to be seven hand-written `document.addEventListener` blocks in
 * `actionDelegator.ts`, and because it was code, nothing could check it —
 * including the checker that was supposed to.
 *
 * `check-inline-js` derived the set of valid attributes by **regexing the
 * delegator's own source** for `handleEvent("data-on-…")`. That worked in one
 * direction only (emitted attribute → is there a listener?) and it happily
 * matched listeners that nothing emitted, so it positively reported four dead
 * listeners as live. `data-on-enter`, `data-on-input`, `data-on-focus` and
 * `data-on-blur` accumulated with zero emitters in either app *and* zero in the
 * legacy PHP they were written for — two of them registered with `capture: true`,
 * so they ran for every focus and blur on every page for nothing.
 *
 * Reading a declaration instead means both directions are checkable and neither
 * side can be parsed wrong.
 *
 * ⚠️ Emitting an attribute is a wire change. `apps/pictufy` (ECS) and
 * `apps/pictufy-web` (S3/CloudFront) deploy separately, so during a rollout an
 * old bundle meets a new page: add the listener, deploy, *then* emit. Production
 * PHP also still emits `data-action` / `data-on-change` / `data-on-keyup`
 * (verified against the `legacy-php-final` tag), so none of those three may be
 * removed before the cutover in `todo/php-retirement.md`.
 *
 * This module must stay dependency-free — the browser bundle value-imports it.
 */
/**
 * Every delegated event the bundle listens for.
 *
 * Keep this minimal: each entry is an always-on document listener that runs
 * `element.closest()` on every occurrence of its event. Do not add one
 * speculatively — add it when the first emitter needs it.
 */
export const VIEW_EVENT_BINDINGS = [
    { attr: "data-action", event: "click" },
    { attr: "data-on-change", event: "change" },
    { attr: "data-on-keyup", event: "keyup" },
];
/**
 * The `data-action` / `data-on-*` VALUES the delegator implements.
 *
 * The value half was checked in neither direction: `check-inline-js` ran
 * `data-action` values against the handler map but never ran `data-on-*` values
 * against anything, so `data-on-change="bogus"` was silently inert. Declaring the
 * names here lets both be checked against one list.
 *
 * `navigate-value` is deliberately here despite never appearing as a
 * `data-action`: it reads `event.target.value`, so it is only meaningful on
 * `data-on-change`.
 */
export const VIEW_DISPATCH_KINDS = [
    "call",
    "hide-and-msg",
    "hide-element",
    "modal",
    "navigate",
    "navigate-value",
    "open",
    "scroll-top",
];
//# sourceMappingURL=viewEvents.js.map